import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStonadConfig } from '../../config/stonadConfigs';
import { useSoknadWizard } from '../../hooks/useSoknadWizard';
import { useDraftSave } from '../../hooks/useDraftSave';
import { WizardProgress } from '../../components/soknad';
import { authFetch } from '../../utils/auth';
import { rateLimitMessage } from '../../utils/rateLimitMessage';
import WizardStep from './WizardStep';

// Async-generation poll cadence (Path-3). POST returns 202 fast, then we poll
// the lightweight status endpoint until the detached backend job reports
// done/failed. The hard cap (~6 min) aligns with the backend stale window so
// the UI never spins forever.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 6 * 60 * 1000;
const GEN_FAILED_MESSAGE =
  'Noe gikk galt da jeg skulle lage søknaden. Svarene dine er lagret, så vi mister ingenting.';
const GEN_TIMEOUT_MESSAGE =
  'Dette tar dessverre uvanlig lang tid akkurat nå. Svarene dine er trygt lagret – prøv forhåndsvisningen om litt, eller prøv igjen.';
// 429 copy for the generate POST (routes/soknad.js limiter 'soknad/generate');
// other limiters get the generic fallback (utils/rateLimitMessage.js).
const GENERATE_RATE_LIMIT = {
  ownLimiter: 'soknad/generate',
  lead: 'Det har vært mange forsøk på kort tid.',
  shortWait: 'Vent litt, så kan du prøve igjen.',
};

// The structural steps (intro, Tier-2 confirm) are NOT wizard "stages" — they
// carry no checkedItems/freeText answers and their ids are not in the backend's
// VALID_STAGES whitelist. Persisting them via the per-stage PUT would 400. Their
// data is saved separately (the picked subject via saveSubject). Only real
// answer steps go through the stage autosave.
const isAnswerStep = (step) =>
  Boolean(step) && !step.isIntroStep && !step.isSubjectConfirmStep;

// "Klar": the søknad text has been generated.
const READY_STATUSES = ['generated', 'downloaded'];

// Does a stage's saved data have any real content?
const stageHasData = (stage) =>
  Boolean(stage) && (
    stage.checkedItems?.length > 0 ||
    (typeof stage.freeText === 'string' && stage.freeText.trim().length > 0) ||
    Boolean(stage.primaryHelper) ||
    Boolean(stage.estimatedHours) ||
    (stage.gateAnswer !== null && stage.gateAnswer !== undefined) ||
    (stage.conditionalData && Object.keys(stage.conditionalData).length > 0)
  );

// Resolve where to resume, robust to the step-array changing across versions
// (PR: person-picker prepended intro@0 + subjectConfirm@1). The saved
// currentStepIndex is a positional index that may be stale for drafts created
// before the prepend, so prefer a DATA-driven cursor: the config index of the
// LAST answer step (in config order) that has saved data. This maps by stage id
// and is correct under any renumbering. If no stage has data, fall back to the
// raw saved index, clamped to the array bounds.
function resolveResumeIndex(steps, stageData, savedIndex) {
  // No config available (shouldn't happen) → just use the saved index.
  if (!steps) {
    return typeof savedIndex === 'number' ? savedIndex : 0;
  }
  let lastWithData = -1;
  steps.forEach((step, idx) => {
    if (isAnswerStep(step) && stageHasData(stageData?.[step.id])) {
      lastWithData = idx;
    }
  });
  if (lastWithData >= 0) return lastWithData;
  if (typeof savedIndex === 'number' && savedIndex >= 0 && savedIndex < steps.length) {
    return savedIndex;
  }
  return 0;
}

/**
 * SoknadWizard - Main wizard container component
 *
 * Reads stonadType and the draft from the route (/soknad/:type/:draftId)
 * and renders the appropriate wizard configuration.
 *
 * Features:
 * - Edits the draft in the URL. The overview (/soknad/:type) picks the child
 *   and creates the draft; the draft's child is fixed here.
 * - Auto-saves stage data when navigating between steps
 * - Shows save status indicator
 */
export default function SoknadWizard() {
  const { type, draftId: routeDraftId } = useParams();
  const navigate = useNavigate();

  // Get the configuration for this stonad type
  const config = getStonadConfig(type);

  // Initialize wizard state with the config
  const wizard = useSoknadWizard(config);

  // Initialize draft persistence
  const draft = useDraftSave(type, routeDraftId);

  // Track previous step to detect navigation
  const prevStepRef = useRef(null);

  // The answer step the parent just changed; the autosave effect saves it
  // once the change has rendered.
  const changedStepRef = useRef(null);

  // Track if we've loaded the initial data
  const hasLoadedRef = useRef(false);

  // Resume cursor (PR: person-picker). The jump is DEFERRED to a second effect
  // that runs after the restored subject has committed, so goToStep's closure
  // sees the correct subjectIsOnboardedChild (the conditional confirm step's
  // active state). Computing it in the same synchronous tick as setSubject would
  // read a stale subject. null = nothing to resume / already resumed.
  const pendingResumeIndexRef = useRef(null);
  const hasResumedRef = useRef(false);

  // Async-generation polling: interval id + mounted guard so the poll loop can
  // never run (or set state) after navigation/unmount.
  const pollRef = useRef(null);
  const isMountedRef = useRef(true);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  // Tier-2 advance gate (PR follow-up): true when the confirm step's picked
  // relation is one the user can't file a barn-søknad for (an adult, or "Dette
  // er meg"). Disables "Neste" so the only way forward is back to the picker.
  // SubjectConfirmStep reports this up and clears it on unmount, so it can't
  // leave a stale block when the step isn't shown.
  const [subjectBlocked, setSubjectBlocked] = useState(false);

  // Check if draft has existing stage data (user has been through wizard before)
  // If so, all steps should be navigable
  const hasExistingStageData = React.useMemo(() => {
    if (!wizard.stageData) return false;

    // Check if any stage has checkedItems with data
    return Object.values(wizard.stageData).some(stage =>
      stage?.checkedItems?.length > 0 ||
      stage?.freeText?.trim().length > 0 ||
      stage?.primaryHelper ||
      stage?.gateAnswer !== null
    );
  }, [wizard.stageData]);

  // Load initial stage data + restore the subject when the draft is ready. The
  // actual resume JUMP is deferred (see the resume effect below) so it runs with
  // the restored subject committed.
  useEffect(() => {
    if (draft.initialStageData && !draft.isLoading && !hasLoadedRef.current) {
      // Load each stage's data into the wizard
      Object.entries(draft.initialStageData).forEach(([stageId, stageData]) => {
        if (stageData && Object.keys(stageData).length > 0) {
          wizard.updateStageData(stageId, stageData);
        }
      });

      // Restore the picked subject.
      if (draft.initialSubject) {
        wizard.setSubject(draft.initialSubject);
      }

      // Compute the resume cursor from DATA, not the raw saved index. The saved
      // currentStepIndex is a config.steps position, but the step array changed
      // when intro@0 + subjectConfirm@1 were prepended — a draft created before
      // this PR stored an index under the OLD numbering, so trusting it verbatim
      // lands on the wrong step (off by the two new steps). Instead, resume at
      // the furthest-progressed answer step that actually has data: that maps by
      // stage id and is correct regardless of old/new numbering. Fall back to the
      // raw saved index only when no stage has data yet.
      // A klar draft ("Rediger svar") opens on the summary instead: every
      // answer with its own "Rediger", and "Lag søknaden" to regenerate.
      pendingResumeIndexRef.current = READY_STATUSES.includes(draft.initialStatus)
        ? config.steps.findIndex((step) => step.isSummaryStep)
        : resolveResumeIndex(config.steps, draft.initialStageData, draft.initialStepIndex);

      hasLoadedRef.current = true;
    }
  }, [draft.initialStageData, draft.isLoading, draft.initialStepIndex, draft.initialStatus, draft.initialSubject, config.steps, wizard]);

  // Deferred resume jump (PR: person-picker). Must run on a commit where the
  // restored subject has ALREADY been applied, so goToStep's closure reads the
  // correct subjectIsOnboardedChild and never skips a Tier-2 confirm step the
  // user left off on. The guard below blocks it from firing in the same commit
  // as the load effect (where setSubject is only scheduled, not yet committed):
  // we wait until wizard.subject reflects the restored value. Fires once.
  useEffect(() => {
    if (hasResumedRef.current) return;
    if (pendingResumeIndexRef.current == null) return;
    // Has the restored subject committed yet? Compare the field that drives
    // conditional-step activeness. If a subject was restored, only proceed once
    // wizard.subject mirrors it.
    if (draft.initialSubject &&
        wizard.subject.subjectIsOnboardedChild !== draft.initialSubject.subjectIsOnboardedChild) {
      return; // setSubject not committed yet — wait for the next commit.
    }
    const target = pendingResumeIndexRef.current;
    pendingResumeIndexRef.current = null;
    hasResumedRef.current = true;
    if (target > 0) {
      wizard.goToStep(target);
    }
  }, [wizard.subject, draft.initialSubject, wizard]);

  // Autosave a change once it has rendered, so the debounced save gets the
  // data after the change (the change handler only sees the data before it).
  // An effect still pending from an earlier render can run right after the
  // handler; it sees the data from before the change and must leave it.
  const { draftId, saveStageDebounced } = draft;
  useEffect(() => {
    const changed = changedStepRef.current;
    if (!changed || !draftId || wizard.stageData === changed.stageDataBefore) return;
    changedStepRef.current = null;
    saveStageDebounced(changed.id, wizard.stageData[changed.id], changed.index);
  }, [wizard.stageData, draftId, saveStageDebounced]);

  // Auto-save when navigating between steps
  useEffect(() => {
    const prevStep = prevStepRef.current;
    const currentStep = wizard.currentStep;

    // Save the previous step's data when step changes (real answer steps only;
    // intro/confirm are not stages).
    if (prevStep && isAnswerStep(prevStep) && prevStep.id !== currentStep?.id && draft.draftId) {
      const stageData = wizard.stageData[prevStep.id];
      if (stageData) {
        draft.saveStageNow(prevStep.id, stageData, wizard.currentStepIndex);
      }
    }

    prevStepRef.current = currentStep;
  }, [wizard.currentStep, wizard.currentStepIndex, wizard.stageData, draft]);

  // Scroll to top when step changes (after React re-renders)
  useEffect(() => {
    const el = document.getElementById('main-scroll-container');
    if (el) el.scrollTop = 0;
  }, [wizard.currentStepIndex]);

  // Handle invalid stonad type
  useEffect(() => {
    if (!config) {
      console.error(`Invalid stonad type: ${type}`);
    }
  }, [config, type]);

  // A draft id that isn't one of the user's drafts goes to the overview. A
  // replace, so the bad URL isn't left in history.
  useEffect(() => {
    if (draft.notFound) {
      navigate(`/soknad/${type}`, { replace: true });
    }
  }, [draft.notFound, type, navigate]);

  // Stop any active generation poll on unmount. The effect body re-arms the
  // mounted flag so StrictMode's mount→unmount→mount cycle leaves it true.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  if (!config) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-semibold text-text-primary mb-4">
            Ukjent søknadstype
          </h1>
          <p className="text-text-secondary mb-6">
            Vi fant ikke søknadstypen "{type}".
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors"
          >
            Tilbake til forsiden
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while draft initializes (and while an unknown draft id
  // redirects)
  if (draft.isLoading || draft.notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-text-secondary">Laster kladd...</p>
        </div>
      </div>
    );
  }

  const handleNext = async () => {
    // Save current step before navigating (real answer steps only).
    if (draft.draftId && isAnswerStep(wizard.currentStep)) {
      const stageData = wizard.stageData[wizard.currentStep.id];
      await draft.saveStageNow(wizard.currentStep.id, stageData, wizard.currentStepIndex + 1);
    }
    // Leaving the Tier-2 confirm step: flush any pending debounced subject save
    // so the last keystroke isn't lost mid-debounce.
    if (draft.draftId && wizard.currentStep?.isSubjectConfirmStep) {
      await draft.flushPendingSaves();
    }

    // Navigate to next step (summary step handles its own completion via handleGenerate)
    wizard.goToNext();
  };

  const handleBack = async () => {
    // Save current step before navigating back (real answer steps only).
    if (draft.draftId && isAnswerStep(wizard.currentStep)) {
      const stageData = wizard.stageData[wizard.currentStep.id];
      await draft.saveStageNow(wizard.currentStep.id, stageData, Math.max(0, wizard.currentStepIndex - 1));
    }
    if (draft.draftId && wizard.currentStep?.isSubjectConfirmStep) {
      await draft.flushPendingSaves();
    }

    if (wizard.isFirstStep) {
      // Navigate back to dashboard
      navigate('/home');
    } else {
      wizard.goBack();
    }
  };

  // Debounced save when stage data changes
  const handleStageDataChange = (changeType, ...args) => {
    // Apply the change to wizard state
    switch (changeType) {
      case 'checkbox':
        wizard.toggleCheckbox(...args);
        break;
      case 'freeText':
        wizard.setFreeText(...args);
        break;
      case 'helperFields':
        wizard.setHelperFields(...args);
        break;
      case 'gateAnswer':
        wizard.setGateAnswer(...args);
        break;
      case 'conditionalData':
        wizard.setConditionalData(...args);
        break;
      default:
        return;
    }

    // Debounced save (collects changes for 1 second), scheduled by the
    // autosave effect once this change has rendered
    if (isAnswerStep(wizard.currentStep)) {
      changedStepRef.current = {
        id: wizard.currentStep.id,
        index: wizard.currentStepIndex,
        stageDataBefore: wizard.stageData,
      };
    }
  };

  // Stop polling helper.
  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Poll the lightweight status endpoint until generation resolves. A single
  // failed poll is non-fatal (just wait for the next tick); the hard cap stops
  // an indefinite spinner. Mirrors the resilient interval pattern in Status.js.
  const startGenerationPolling = () => {
    const startedAt = Date.now();

    const finishWithError = (message, rawError) => {
      stopPolling();
      if (rawError) console.log('[SoknadWizard] generationError:', rawError);
      if (!isMountedRef.current) return;
      setIsGenerating(false);
      setGenerateError(message);
    };

    const goToPreview = () => {
      stopPolling();
      if (!isMountedRef.current) return;
      navigate(`/soknad/${type}/${draft.draftId}/preview`);
    };

    // Act on a status payload. Returns true if a terminal state was reached.
    // We trust generationState first; status is only a fallback for a
    // pre-existing generated draft (never while a job is actively running, so a
    // re-generation can't navigate to stale content).
    const handleStatus = (data) => {
      const gs = data?.generationState;
      if (gs === 'done') {
        goToPreview();
        return true;
      }
      if (gs === 'failed') {
        finishWithError(GEN_FAILED_MESSAGE, data?.generationError);
        return true;
      }
      if (gs === 'running') {
        return false; // keep polling
      }
      if (data?.status === 'generated' && data?.hasFullText) {
        goToPreview();
        return true;
      }
      return false;
    };

    const tick = async () => {
      // Hard cap: stop the interval, do one final status check.
      if (Date.now() - startedAt > POLL_MAX_MS) {
        stopPolling();
        try {
          const res = await authFetch(`/soknad/${type}/drafts/${draft.draftId}/status`);
          const data = await res.json();
          if (handleStatus(data)) return;
        } catch (e) {
          // ignore — fall through to the timeout message
        }
        if (!isMountedRef.current) return;
        setIsGenerating(false);
        setGenerateError(GEN_TIMEOUT_MESSAGE);
        return;
      }

      // Regular poll. authFetch throws on non-OK — swallow and retry next tick.
      try {
        const res = await authFetch(`/soknad/${type}/drafts/${draft.draftId}/status`);
        const data = await res.json();
        handleStatus(data);
      } catch (e) {
        // transient poll failure — retry on the next interval
      }
    };

    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
  };

  // Handle generate button click from summary step. Path-3 async flow: POST
  // returns 202 quickly (the AI work runs in the background), then we poll the
  // status endpoint until done/failed (or the hard cap).
  const handleGenerate = async () => {
    if (!draft.draftId) {
      setGenerateError('Ingen kladd funnet. Vennligst last siden på nytt.');
      return;
    }

    stopPolling();
    setIsGenerating(true);
    setGenerateError(null);

    try {
      // Save current stage data before generating (real answer steps only;
      // generate is triggered from the summary step, which is a valid stage).
      if (isAnswerStep(wizard.currentStep)) {
        const stageData = wizard.stageData[wizard.currentStep.id];
        await draft.saveStageNow(wizard.currentStep.id, stageData, wizard.currentStepIndex);
      }

      // Kick off generation. Returns 202 well under the SWA proxy timeout, so
      // no directBackend bypass is needed. 202 is response.ok, so authFetch
      // does not throw; the body is just the claim ack — the real result
      // arrives via polling.
      const response = await authFetch(`/soknad/${type}/drafts/${draft.draftId}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      await response.json().catch(() => ({}));

      // Begin polling for completion.
      startGenerationPolling();
    } catch (error) {
      // The POST itself failed (network, or authFetch threw on a non-OK
      // status). Surface it, never a silent return.
      console.error('[SoknadWizard] Generation start error:', error);
      stopPolling();
      setIsGenerating(false);
      // 429: nothing was claimed or generated; answers and draft are intact,
      // so "Prøv igjen" later simply re-POSTs.
      setGenerateError(error?.status === 429
        ? rateLimitMessage(error, GENERATE_RATE_LIMIT)
        : GEN_FAILED_MESSAGE);
    }
  };

  // Handle edit step navigation from summary
  const handleEditStep = (stepIndex) => {
    wizard.goToStep(stepIndex);
  };

  // Tier-2 confirm step: user confirmed the subject's relation/age/diagnoses.
  // These are facts the USER typed — never parsed from memory. Fires on every
  // keystroke, so persist through the DEBOUNCED subject save (collapses rapid
  // edits to one PUT; each call carries the full identity so last-write-wins).
  const handleConfirmSubjectFacts = (subjectIdentity) => {
    wizard.setSubject({ subjectIdentity });
    if (draft.draftId) {
      draft.saveSubjectDebounced({ subjectIdentity });
    }
  };

  // Tier-2 gate: the child is picked on the overview, so go back there to pick
  // the child instead of the blocked adult/self subject.
  const handleBackToPicker = () => {
    navigate(`/soknad/${type}`);
  };

  // Handle step dot click navigation (with auto-save). The dot index is a
  // position among the VISIBLE (active) steps, so map it back to the real
  // config.steps index before navigating/saving.
  const handleStepDotClick = async (activePosition) => {
    const targetStep = wizard.activeSteps[activePosition];
    if (!targetStep) return;
    const configIndex = config.steps.findIndex(s => s.id === targetStep.id);
    if (configIndex === -1) return;

    // Don't navigate to current step
    if (configIndex === wizard.currentStepIndex) return;

    // Save current step data before navigating (real answer steps only).
    if (draft.draftId && isAnswerStep(wizard.currentStep)) {
      const stageData = wizard.stageData[wizard.currentStep.id];
      if (stageData) {
        await draft.saveStageNow(wizard.currentStep.id, stageData, configIndex);
      }
    }

    // Navigate to the clicked step
    wizard.goToStep(configIndex);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4">
        {/* Page header */}
        <div className="pt-8 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-text-primary">
                Søknadshjelper
              </h1>
              <p className="text-text-secondary mt-1">
                {config.displayName} — vi hjelper deg steg for steg
              </p>
            </div>
            {/* Save status indicator. "Lagret" only once the server has
                confirmed the latest change: nothing waiting, nothing on its
                way, no error. */}
            <div className="text-sm text-text-muted">
              {(draft.isSaving || draft.hasPendingChanges) && (
                <span className="flex items-center">
                  <span className="animate-pulse mr-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </span>
                  Lagrer…
                </span>
              )}
              {!draft.isSaving && !draft.hasPendingChanges && !draft.saveError && draft.lastSavedAt && (
                <span className="flex items-center text-sage-600">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Lagret
                </span>
              )}
              {!draft.isSaving && !draft.hasPendingChanges && draft.saveError && (
                <span className="text-error-600">{draft.saveError}</span>
              )}
            </div>
          </div>
        </div>

        <div className="pb-8">
          {/* Progress bar. currentStep is the 1-based position among the
              VISIBLE (active) steps so a skipped Tier-2 confirm step doesn't
              leave a gap or miscount "Steg X av Y". */}
          <WizardProgress
            currentStep={
              wizard.activeSteps.findIndex(s => s.id === wizard.currentStep?.id) + 1
            }
            totalSteps={wizard.totalSteps}
            showDots={wizard.totalSteps <= 10}
            allStepsNavigable={hasExistingStageData}
            onStepClick={handleStepDotClick}
          />

          {/* Step content card */}
          <div
            key={wizard.currentStepIndex}
            className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-border-light step-fade-in"
          >
            <WizardStep
              step={wizard.currentStep}
              stageData={wizard.currentStageData}
              helperOptions={config.helperOptions}
              onToggleCheckbox={(itemId) => handleStageDataChange('checkbox', itemId)}
              onSetFreeText={(text) => handleStageDataChange('freeText', text)}
              onSetHelperFields={(helper, hours) => handleStageDataChange('helperFields', helper, hours)}
              onSetGateAnswer={(answer) => handleStageDataChange('gateAnswer', answer)}
              onSetConditionalData={(key, value) => handleStageDataChange('conditionalData', key, value)}
              // Summary step props
              config={config}
              allStageData={wizard.stageData}
              onEditStep={handleEditStep}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              generateError={generateError}
              // Subject (person-picker) props
              subject={wizard.subject}
              onConfirmSubjectFacts={handleConfirmSubjectFacts}
              onSubjectBlockedChange={setSubjectBlocked}
              onBackToPicker={handleBackToPicker}
              onAdvance={handleNext}
            />
          </div>

          {/* Navigation buttons - hidden on the summary step (own generate
              button) and the intro step (own "La oss begynne" CTA). */}
          {!wizard.currentStep?.isSummaryStep && !wizard.currentStep?.isIntroStep && (
            <div className="flex justify-between items-center mt-8">
              {/* Back button */}
              <button
                type="button"
                onClick={handleBack}
                disabled={draft.isSaving}
                className="
                  flex items-center px-4 py-3 rounded-xl
                  text-text-secondary font-medium
                  hover:bg-neutral-100 transition-colors
                  disabled:opacity-50
                "
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Tilbake
              </button>

              {/* Next button — also disabled when the Tier-2 confirm step has a
                  blocked subject (adult / "Dette er meg"); the user must go back
                  to the picker via the in-step button. */}
              <button
                type="button"
                onClick={handleNext}
                disabled={draft.isSaving || subjectBlocked}
                className="
                  flex items-center px-8 py-3 rounded-xl
                  bg-primary text-white font-semibold
                  hover:bg-primary-600 hover:shadow-primary
                  active:transform active:scale-95
                  transition-all duration-200
                  min-w-[140px] justify-center
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Neste
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
